import { Icon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Flex,
  Modal,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Stack,
  useBreakpointValue
} from '@chakra-ui/react'
import ExternalLink from 'components/links/ExternalLink'
import UserIcon from 'components/svg/UserIcon'
import WhatsApp from 'components/svg/WhatsApp'
import { H2, Label } from 'components/typography'
import { MEDLEVE_WHATSAPP_LINK } from 'constants/omensSupportContact'
import Cookies from 'js-cookie'
import Link from 'next/link'
import { FC, RefObject, useCallback, useRef } from 'react'
import { MdLogout } from 'react-icons/md'
import { signOut } from 'redux/slices/auth-slice'
import { useAppDispatch, useAppSelector } from 'redux/store'

interface HomeMobileMenuProps {
  isOpen: boolean
  onClose: () => void
  buttonRef: RefObject<HTMLButtonElement>
}

interface MenuLinkProps {
  title: string
  href: string
  isCurrent?: boolean
  onClose: () => void
}

const MenuLink: FC<MenuLinkProps> = ({ title, href, isCurrent = false, onClose }) => (
  <Box px={4} py={2}>
    <Link href={href} prefetch={false} onClick={onClose} aria-current={isCurrent ? 'page' : undefined}>
      <H2
        as="span"
        display="inline-block"
        color="alabaster"
        borderBottom="2px solid"
        borderColor={isCurrent ? 'medleve' : 'transparent'}
      >
        {title}
      </H2>
    </Link>
  </Box>
)

/**
 * Mobile menu of the organic homepage: the Medleve branch of the shared `NavbarModal`, isolated
 * (PRD §33). Same entries, same destinations. It only renders once opened, i.e. always after hydration,
 * so reading the persisted login state here cannot cause a server/client mismatch.
 */
const HomeMobileMenu: FC<HomeMobileMenuProps> = ({ isOpen, onClose, buttonRef }) => {
  const dispatch = useAppDispatch()
  const isLoggedIn = useAppSelector((state) => state.auth.isLoggedIn)
  const size = useBreakpointValue({ base: 'full', md: 'md' })
  const portalRef = useRef<HTMLDivElement>(null)

  const handleSignOut = useCallback(() => {
    // Limpa todos os cookies antes de fazer logout
    const cookies = Cookies.get()
    Object.keys(cookies).forEach((cookieName) => {
      Cookies.remove(cookieName, { path: '/' })
      Cookies.remove(cookieName, { path: '/', domain: '.omens.com.br' })
    })
    dispatch(signOut())
  }, [dispatch])

  return (
    <>
      <Box ref={portalRef} position="absolute" zIndex={-1} />
      <Modal
        {...{ isOpen, onClose, size }}
        finalFocusRef={buttonRef}
        closeOnOverlayClick={false}
        trapFocus={false}
        portalProps={{ containerRef: portalRef }}
      >
        <ModalOverlay backdropFilter="blur(54px)" pointerEvents="none" bg="blackAlpha.300" zIndex="-1" />
        <ModalContent bg="transparent" borderRadius="0" boxShadow="none">
          <Stack as="nav" aria-label="Menu" mt="110px" mx={8} pb={4} spacing={2}>
            <MenuLink onClose={onClose} title="Início" href="/" isCurrent />
            <MenuLink onClose={onClose} title="Dúvidas" href="/faq" />
            <MenuLink onClose={onClose} title="Contato" href={MEDLEVE_WHATSAPP_LINK} />
          </Stack>

          <ModalFooter p={3} my="24px">
            <Flex flexDir={isLoggedIn ? 'column' : 'row'} w="100%" gap="12px">
              {isLoggedIn && (
                <Link href="/me" prefetch={false} onClick={onClose}>
                  <Button shadow="none" h="48px" borderRadius="full" bg="alabaster" w="full">
                    <UserIcon fill="phthalo" />
                    <Label>Minha conta</Label>
                  </Button>
                </Link>
              )}

              <Flex justifyContent="space-between" w="full" gap="12px">
                {isLoggedIn ? (
                  <Button
                    leftIcon={<Icon color="alabaster" m="0 !important" as={MdLogout} />}
                    onClick={handleSignOut}
                    bg="rgba(246, 244, 232, 0.1)"
                    shadow="none"
                    size="lg"
                    w="50%"
                  >
                    <Label color="alabaster">Sair</Label>
                  </Button>
                ) : (
                  <Box flex="1">
                    <Link href="/me" prefetch={false}>
                      <Button w="full" size="lg" leftIcon={<UserIcon maxW="0.75rem" />} bg="alabaster" shadow="none">
                        <Label>Login</Label>
                      </Button>
                    </Link>
                  </Box>
                )}
                <Box w="50%">
                  <ExternalLink href={MEDLEVE_WHATSAPP_LINK}>
                    <Button
                      bg="rgba(246, 244, 232, 0.1)"
                      leftIcon={<WhatsApp fontSize="20px" color="alabaster" />}
                      w="100%"
                      shadow="none"
                      size="lg"
                      color="alabaster"
                    >
                      <Label color="alabaster">Chat</Label>
                    </Button>
                  </ExternalLink>
                </Box>
              </Flex>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default HomeMobileMenu
